import React from 'react';
import styled from '@emotion/styled';
import { switchProp } from 'styled-tools';
import { useUtils } from '~/shared/hooks/useUtils';
import { useTranslation } from '~/shared/utils/translation';
import { useFrame } from '~/shared/utils';
import { PromotionType } from '~/lib/data-contract/salesforce';
import {
    SalesforceProductPrice,
    SalesforceProductPriceType as PriceTypes,
} from '~/lib/salesforce/types';
import { getSchemaPrice } from '~/shared/utils/schema-price';
import { shouldHighlightDefaultPromoPrice } from '~/shared/utils/shouldHighlightDefaultPromoPrice';

export const PriceWrap = styled.div(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    margin: `${theme.space[3]} 0 0`,
}));

export const PriceItem = styled.div({
    textAlign: 'center',

    '&:first-of-type:not(:only-of-type)': {
        textAlign: 'left',
    },

    '& + &': {
        marginLeft: 'auto',
        textAlign: 'right',
    },
});

/**
 * Aliases rather than copies. `PriceTypes` and `TPrice` described exactly what
 * `lib/salesforce/types` describes, but as separate nominal types, so the two could never be
 * assigned to one another. Re-exported so the many `PriceTypes.SALE` call sites keep working.
 *
 * TODO(IMC-1671): Resolve imports to `lib/salesforce/types` instead of `/ProductCard/components/Price.tsx`.
 */
export { PriceTypes };
export type TPrice = SalesforceProductPrice;

export interface ProductPrice {
    beforeValue?: number;
    value: string;
}

export const Price = styled.div<Pick<TPrice, 'type'> & { align?: 'left' | 'right' | 'center' }>(
    ({ theme, align }) => ({
        ...theme.mixins.useTextStyle('productPrice'),
        display: 'block',
        textAlign: align,
    }),
    switchProp(
        'type',
        {
            default: ({ theme }) => ({
                color: theme.colors.black,
            }),
            sale: ({ theme }) => ({
                color: theme.colors.onSale,
            }),
            disabled: ({ theme }) => ({
                color: theme.colors.grey50,
                textDecoration: 'line-through',
            }),
        },
        'default',
    ),
);

export const PriceLabel = styled.div<
    Pick<TPrice, 'type'> & { align?: 'left' | 'right' | 'center' }
>(
    ({ theme, align }) => ({
        ...theme.mixins.useTextStyle('productInfo'),
        color: theme.colors.black,
        display: 'block',
        textAlign: align,
    }),
    switchProp(
        'type',
        {
            sale: ({ theme }) => ({
                color: theme.colors.onSale,
            }),
            disabled: ({ theme }) => ({
                color: theme.colors.grey50,
                textDecoration: 'strikethrough',
            }),
        },
        'default',
    ),
);

export const Prices = ({
    prices,
    defaultLabel,
    isCampaignRedirect = false,
    promoType,
}: {
    defaultLabel?: string;
    prices: TPrice[];
    promoType?: string;
    isCampaignRedirect?: boolean;
}) => {
    if (prices.length == 0) {
        return null;
    }

    const { convertPriceToLocale } = useUtils();
    const { data } = useFrame();
    const { translate } = useTranslation();
    const hasMemberPrice = prices.some((price) => price.promotype === PromotionType.MEMBER_PRICE);

    const schemaPrice = getSchemaPrice(prices);

    const getDisplayType = (price: TPrice): PriceTypes => {
        const shouldHighlightDefaultPrice = shouldHighlightDefaultPromoPrice({
            priceType: price.type,
            promoType,
            prices,
        });

        return shouldHighlightDefaultPrice ? PriceTypes.SALE : price.type ?? PriceTypes.DEFAULT;
    };

    const sortedPrices = [...prices]
        .map((price) => ({ ...price, displayType: getDisplayType(price) }))
        .sort((a, b) => {
            const order = {
                [PriceTypes.DEFAULT]: 0,
                [PriceTypes.DISABLED]: 0,
                [PriceTypes.SALE]: 1,
            };

            return order[a.displayType] - order[b.displayType];
        });

    const shouldUseMemberPriceLabels =
        sortedPrices.length > 1 && !isCampaignRedirect && hasMemberPrice;

    const getPriceLabel = (price: TPrice): string | undefined => {
        if (!shouldUseMemberPriceLabels) return price.label ?? defaultLabel;

        return price.promotype === PromotionType.MEMBER_PRICE
            ? translate('pdp.memberPrice')
            : translate('pdp.notMemberPrice');
    };

    return (
        <PriceWrap>
            <meta itemProp="priceCurrency" content={data.market.currency?.currency} />
            <meta itemProp="price" content={String(schemaPrice?.value)} />
            {sortedPrices.map((price, index) => (
                <PriceItem key={`${price.value + index.toString() + 1}`}>
                    <PriceLabel type={price.displayType}>{getPriceLabel(price)}</PriceLabel>
                    <Price type={price.displayType}>{convertPriceToLocale(price.value)}</Price>
                </PriceItem>
            ))}
        </PriceWrap>
    );
};
